import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Truck, PackageCheck, Ban, Receipt, IndianRupee, Download, X, Pencil, Wrench, Plus, ExternalLink, Trash2 } from "lucide-react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import { canAccounts, canProcure, canSalesWrite, useAuth } from "@/lib/auth";
import { SALES } from "@/constants/testIds";

const STATUS_COLORS = {
  DRAFT: "bg-slate-100 text-slate-700",
  CONFIRMED: "bg-sky-100 text-sky-800",
  PACKED: "bg-indigo-100 text-indigo-800",
  DISPATCHED: "bg-violet-100 text-violet-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-rose-100 text-rose-800",
};
const PAY_COLORS = {
  UNPAID: "bg-rose-100 text-rose-800",
  PARTIAL: "bg-amber-100 text-amber-800",
  PAID: "bg-emerald-100 text-emerald-800",
};

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-slate-900`}>{value}</span>
    </div>
  );
}

export default function SalesDetailPage() {
  const { so_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const [modal, setModal] = useState(null); // "cancel" | "pay" | "edit"
  const [cancelReason, setCancelReason] = useState("");
  const [pay, setPay] = useState({
    method: "CASH", amount: "", reference: "", remarks: "",
  });

  // V1.2 — Sales-invoice editing form state (opened from "Edit Invoice" button).
  const [edit, setEdit] = useState(null);
  // V2.8 — DRAFT-only line-items editing state (inside Edit Invoice modal).
  const [editLines, setEditLines] = useState([]);
  const [invQuery, setInvQuery] = useState("");
  const [invResults, setInvResults] = useState([]);
  const [invSearching, setInvSearching] = useState(false);
  const [invError, setInvError] = useState(null);

  // V1.2 — Customer Cases attached to this Sales Invoice.
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/sales/${so_id}`);
      setDetail(data);
    } catch (e) {
      setErr(errorToMessage(e, "Failed to load sales order"));
    } finally {
      setLoading(false);
    }
  }, [so_id]);

  useEffect(() => { load(); }, [load]);

  // V1.2 — Fetch customer cases attached to this Sales Invoice.
  const loadCases = useCallback(async () => {
    if (!so_id) return;
    setCasesLoading(true);
    try {
      const { data } = await api.get(`/sales/${so_id}/cases`);
      setCases(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      // Non-fatal — hide the section on failure. Only log in development so
      // we don't pollute the production console.
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to load cases for sales order", e);
      }
      setCases([]);
    } finally {
      setCasesLoading(false);
    }
  }, [so_id]);
  useEffect(() => { loadCases(); }, [loadCases]);

  // Group cases by passport_id for per-line display.
  const casesByPassport = useMemo(() => {
    const map = {};
    for (const c of cases) {
      const k = c.passport_id || "__unlinked__";
      if (!map[k]) map[k] = [];
      map[k].push(c);
    }
    return map;
  }, [cases]);

  const openEditModal = () => {
    if (!detail) return;
    setEdit({
      customer_type: detail.customer_type || "WALK_IN",
      bill_name: detail.bill_name || "",
      bill_mobile: detail.bill_mobile || "",
      bill_email: detail.bill_email || "",
      bill_company_name: detail.bill_company_name || "",
      bill_gstin: detail.bill_gstin || "",
      billing: {
        address_line1: detail.bill?.address_line1 || "",
        address_line2: detail.bill?.address_line2 || "",
        city: detail.bill?.city || "",
        state: detail.bill?.state || "",
        pincode: detail.bill?.pincode || "",
      },
      shipping: detail.ship ? {
        name: detail.ship.name || "",
        mobile: detail.ship.mobile || "",
        address_line1: detail.ship.address_line1 || "",
        address_line2: detail.ship.address_line2 || "",
        city: detail.ship.city || "",
        state: detail.ship.state || "",
        pincode: detail.ship.pincode || "",
      } : null,
      remarks: detail.remarks || "",
    });
    // V2.8 — Seed the DRAFT line-editor with a copy of the existing lines.
    // Non-DRAFT invoices ignore this state (line-editor section is hidden).
    setEditLines((detail.lines || []).map((l) => ({
      passport_id: l.passport_id,
      description: l.description || "",
      passport_barcode: l.passport_barcode || "",
      passport_serial: l.passport_serial || "",
      unit_price: String(l.unit_price ?? ""),
      discount_amount: String(l.discount_amount ?? "0"),
      gst_rate_pct: String(l.gst_rate_pct ?? "18"),
    })));
    setInvQuery("");
    setInvResults([]);
    setInvError(null);
    setModal("edit");
  };

  const saveEdit = () => runAction("Save invoice edits", async () => {
    const body = {
      customer_type: edit.customer_type,
      bill_name: edit.bill_name || null,
      bill_mobile: edit.bill_mobile || null,
      bill_email: edit.bill_email || null,
      bill_company_name: edit.bill_company_name || null,
      bill_gstin: edit.bill_gstin || null,
      billing: {
        name: edit.bill_name || null,
        mobile: edit.bill_mobile || null,
        address_line1: edit.billing.address_line1 || null,
        address_line2: edit.billing.address_line2 || null,
        city: edit.billing.city || null,
        state: edit.billing.state || null,
        pincode: edit.billing.pincode || null,
      },
      shipping: edit.shipping ? {
        name: edit.shipping.name || null,
        mobile: edit.shipping.mobile || null,
        address_line1: edit.shipping.address_line1 || null,
        address_line2: edit.shipping.address_line2 || null,
        city: edit.shipping.city || null,
        state: edit.shipping.state || null,
        pincode: edit.shipping.pincode || null,
      } : null,
      remarks: edit.remarks || null,
    };
    // V2.8 — DRAFT-only: also send the (potentially reworked) line items.
    // Backend `_replace_lines` performs a full replacement and recomputes
    // GST/subtotals/grand total. Non-DRAFT invoices never send `lines`
    // (backend now returns a 409 if lines are sent for a non-DRAFT SO).
    if (detail?.status === "DRAFT") {
      body.lines = editLines.map((l) => ({
        passport_id: l.passport_id,
        unit_price: String(l.unit_price || "0"),
        discount_amount: String(l.discount_amount || "0"),
        gst_rate_pct: String(l.gst_rate_pct || "18"),
      }));
    }
    await api.patch(`/sales/${so_id}`, body);
    await loadCases();
  });

  // V2.8 — DRAFT line-editor helpers. Modal-scoped state; no API side effects.
  const searchInventory = async () => {
    const q = invQuery.trim();
    if (!q) return;
    setInvSearching(true);
    setInvError(null);
    setInvResults([]);
    try {
      const { data } = await api.get("/inventory", {
        params: { q, status: "AVAILABLE", page: 1, page_size: 25 },
      });
      setInvResults(data.items || []);
    } catch (e) {
      setInvError(errorToMessage(e, "Search failed"));
    } finally {
      setInvSearching(false);
    }
  };
  const addEditLine = (p) => {
    if (editLines.some((l) => l.passport_id === p.passport_id)) return;
    setEditLines((prev) => [...prev, {
      passport_id: p.passport_id,
      description: `${p.brand || ""} ${p.model || ""}`.trim(),
      passport_barcode: p.internal_barcode || p.passport_barcode || "",
      passport_serial: p.manufacturer_serial || p.passport_serial || "",
      unit_price: "",
      discount_amount: "0",
      gst_rate_pct: "18",
    }]);
    setInvResults((prev) => prev.filter((r) => r.passport_id !== p.passport_id));
  };
  const patchEditLine = (idx, patch) =>
    setEditLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeEditLine = (idx) =>
    setEditLines((prev) => prev.filter((_, i) => i !== idx));

  // V2.8 — Live totals mirror backend `_compute_line` (see NewSalesPage).
  const editTotals = useMemo(() => {
    const toDec = (v, d = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : d;
    };
    const shipState = (edit?.shipping?.state || edit?.billing?.state || "KA").toUpperCase();
    const isIgst = shipState !== "KA";
    let subtotal = 0, discount = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0, grand = 0;
    editLines.forEach((l) => {
      const unit = toDec(l.unit_price);
      const disc = toDec(l.discount_amount);
      const rate = toDec(l.gst_rate_pct, 18);
      const gross = unit * 1;
      const tx = Math.max(0, gross - disc);
      const gst = tx * (rate / 100);
      subtotal += gross;
      discount += disc;
      taxable += tx;
      if (isIgst) igst += gst; else { cgst += gst / 2; sgst += gst / 2; }
      grand += tx + gst;
    });
    return { subtotal, discount, taxable, cgst, sgst, igst, grand, isIgst };
  }, [editLines, edit]);

  // Build the query string used by the "Create New Case" links.
  const buildCaseCreateHref = (passport) => {
    if (!detail) return "/admin/cases/new";
    const p = new URLSearchParams();
    p.set("sales_order_id", detail.id);
    if (detail.invoice_no) p.set("invoice_no", detail.invoice_no);
    if (detail.customer_id) p.set("customer_id", detail.customer_id);
    if (detail.bill_name) p.set("customer_name", detail.bill_name);
    if (detail.bill_mobile) p.set("mobile", detail.bill_mobile);
    if (detail.bill_email) p.set("email", detail.bill_email);
    if (detail.bill_company_name) p.set("company_name", detail.bill_company_name);
    if (detail.bill_gstin) p.set("gst_number", detail.bill_gstin);
    if (detail.bill?.city) p.set("city", detail.bill.city);
    if (detail.bill?.state) p.set("state", detail.bill.state);
    if (detail.bill?.pincode) p.set("pincode", detail.bill.pincode);
    if (detail.bill?.address_line1 || detail.bill?.address_line2) {
      p.set("address", [detail.bill.address_line1, detail.bill.address_line2].filter(Boolean).join(", "));
    }
    if (passport) {
      p.set("passport_id", passport.id);
      if (passport.serial) p.set("manufacturer_serial", passport.serial);
      if (passport.brand) p.set("brand", passport.brand);
      if (passport.model) p.set("model", passport.model);
      if (passport.processor) p.set("processor", passport.processor);
      if (passport.ram) p.set("ram", passport.ram);
      if (passport.storage) p.set("storage", passport.storage);
      if (passport.generation) p.set("generation", passport.generation);
    }
    return `/admin/cases/new?${p.toString()}`;
  };

  const runAction = async (label, fn) => {
    setBusy(true);
    setActionErr(null);
    try {
      await fn();
      await load();
      setModal(null);
    } catch (e) {
      setActionErr(errorToMessage(e, `${label} failed`));
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => runAction("Confirm", async () => {
    await api.post(`/sales/${so_id}/confirm`);
  });
  const dispatch = () => runAction("Dispatch", async () => {
    await api.post(`/sales/${so_id}/dispatch`);
  });
  const complete = () => runAction("Complete", async () => {
    await api.post(`/sales/${so_id}/complete`);
  });
  const cancel = () => runAction("Cancel", async () => {
    if (!cancelReason.trim()) throw new Error("Reason required");
    await api.post(`/sales/${so_id}/cancel`, { reason: cancelReason.trim() });
  });
  const deleteOrder = async () => {
    setBusy(true); setActionErr(null);
    try {
      await api.delete(`/sales/${so_id}`);
      navigate("/admin/sales");
    } catch (e) {
      setActionErr(errorToMessage(e, "Delete failed"));
      setModal(null);
    } finally { setBusy(false); }
  };
  const recordPayment = () => runAction("Record payment", async () => {
    const amt = Number(pay.amount);
    if (!Number.isFinite(amt) || amt <= 0) throw new Error("Amount must be positive");
    await api.post(`/sales/${so_id}/payments`, {
      method: pay.method,
      amount: String(amt),
      reference: pay.reference || null,
      remarks: pay.remarks || null,
    });
    setPay({ method: "CASH", amount: "", reference: "", remarks: "" });
  });
  const genInvoice = () => runAction("Generate invoice", async () => {
    await api.post(`/sales/${so_id}/invoice`);
  });

  const downloadInvoice = () => {
    if (!detail?.invoice_no) return;
    const stored = getStoredAuth();
    const token = stored?.access_token;
    // Use a temporary <a download> with the Authorization header — since
    // FileResponse requires the bearer, we route via fetch → blob.
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/sales/invoices/${detail.invoice_no}.pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${detail.invoice_no}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setActionErr(errorToMessage(e, "Invoice download failed"));
      }
    })();
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (err) return <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>;
  if (!detail) return null;

  const canWrite = canSalesWrite(user);
  const canAcc = canAccounts(user);
  // V1.2 — RECEPTION / ADMIN / SUPER_ADMIN may edit an existing sales invoice.
  const canEditInvoice = canProcure(user) && detail.status !== "CANCELLED";

  const showConfirm = canWrite && detail.status === "DRAFT";
  const showDispatch = canWrite && detail.status === "PACKED";
  const showComplete = canWrite && detail.status === "DISPATCHED";
  const showCancel = canWrite && ["DRAFT","CONFIRMED"].includes(detail.status);
  const showDelete = ["SUPER_ADMIN", "ADMIN"].includes(user?.role)
    && ["DRAFT", "CONFIRMED", "CANCELLED"].includes(detail.status)
    && Number(detail.paid_total || 0) === 0;
  const showInvoice = canAcc && !detail.invoice_no
    && ["CONFIRMED","PACKED","DISPATCHED","COMPLETED"].includes(detail.status);
  const showPay = canAcc && detail.status !== "CANCELLED"
    && detail.payment_status !== "PAID";

  return (
    <div className="space-y-6 pb-10" data-testid="sales-detail-page">
      <div className="flex items-baseline justify-between">
        <div>
          <button onClick={() => navigate("/admin/sales")}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Sales
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900"
            data-testid={SALES.detailHeader}>
            <span className="font-mono">{detail.so_no}</span>
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <span data-testid={SALES.detailStatus}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[detail.status]}`}>
              {detail.status}
            </span>
            <span data-testid={SALES.detailPayStatus}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PAY_COLORS[detail.payment_status]}`}>
              {detail.payment_status}
            </span>
            <span>· {detail.order_date}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end max-w-md">
          {canEditInvoice && (
            <button data-testid="btn-edit-invoice" onClick={openEditModal}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <Pencil size={14} /> Edit Invoice
            </button>
          )}
          {showConfirm && (
            <button data-testid={SALES.confirmBtn} onClick={confirm} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              <CheckCircle2 size={14} /> Confirm
            </button>
          )}
          {showDispatch && (
            <button data-testid={SALES.dispatchBtn} onClick={dispatch} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              <Truck size={14} /> Dispatch
            </button>
          )}
          {showComplete && (
            <button data-testid={SALES.completeBtn} onClick={complete} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40">
              <PackageCheck size={14} /> Complete
            </button>
          )}
          {showCancel && (
            <button data-testid={SALES.cancelBtn} onClick={() => setModal("cancel")}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50">
              <Ban size={14} /> Cancel
            </button>
          )}
          {showDelete && (
            <button data-testid="btn-delete-invoice" onClick={() => setModal("delete")}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50">
              <Trash2 size={14} /> Delete
            </button>
          )}
          {showInvoice && (
            <button data-testid={SALES.invoiceBtn} onClick={genInvoice} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <Receipt size={14} /> Generate invoice
            </button>
          )}
          {detail.invoice_no && (
            <button data-testid={SALES.invoiceDownload} onClick={downloadInvoice}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <Download size={14} /> {detail.invoice_no}
            </button>
          )}
          {showPay && (
            <button data-testid={SALES.payAdd} onClick={() => setModal("pay")}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800">
              <IndianRupee size={14} /> Record payment
            </button>
          )}
        </div>
      </div>

      {actionErr && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionErr}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Customer & addresses */}
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Customer</h2>
          <div className="space-y-1">
            <div className="text-slate-900 font-medium">{detail.bill_name}</div>
            <div className="text-xs text-slate-500">{detail.bill_mobile} · {detail.customer_type}</div>
            {detail.bill_company_name && (
              <div className="text-xs text-slate-500">{detail.bill_company_name}</div>
            )}
            {detail.bill_gstin && (
              <div className="text-xs text-slate-500 font-mono">GSTIN {detail.bill_gstin}</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Billing</div>
            <div className="text-sm text-slate-800">
              {[detail.bill?.address_line1, detail.bill?.address_line2, detail.bill?.city,
                detail.bill?.state, detail.bill?.pincode].filter(Boolean).join(", ") || "—"}
            </div>
          </div>
          {detail.ship && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Shipping</div>
              <div className="text-sm text-slate-800">
                {detail.ship.name} · {detail.ship.mobile}<br />
                {[detail.ship.address_line1, detail.ship.city, detail.ship.state, detail.ship.pincode]
                  .filter(Boolean).join(", ")}
              </div>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
          <h2 className="text-sm font-semibold text-slate-900 mb-2">Totals</h2>
          <Row label="Subtotal" value={`₹${detail.subtotal}`} mono />
          <Row label="Discount" value={`₹${detail.discount_total}`} mono />
          <Row label="Taxable" value={`₹${detail.taxable_total}`} mono />
          {Number(detail.igst_total) > 0
            ? <Row label="IGST" value={`₹${detail.igst_total}`} mono />
            : (<>
                <Row label="CGST" value={`₹${detail.cgst_total}`} mono />
                <Row label="SGST" value={`₹${detail.sgst_total}`} mono />
              </>)}
          <div className="border-t border-slate-100 pt-2">
            <Row label="Grand total" value={<span className="text-lg font-semibold">₹{detail.grand_total}</span>} />
            <Row label="Paid" value={`₹${detail.paid_total}`} mono />
            <Row label="Balance" value={`₹${detail.balance}`} mono />
          </div>
          {detail.remarks && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1">Remarks</div>
              <div className="text-sm text-slate-800">{detail.remarks}</div>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
          <h2 className="text-sm font-semibold text-slate-900 mb-2">Timeline</h2>
          <div className="text-xs text-slate-600 space-y-1.5">
            <div>Invoice date: <span className="text-slate-900">{detail.order_date}</span></div>
            {detail.confirmed_at && <div>Confirmed: <span className="text-slate-900">{new Date(detail.confirmed_at).toLocaleString()}</span></div>}
            {detail.dispatched_at && <div>Dispatched: <span className="text-slate-900">{new Date(detail.dispatched_at).toLocaleString()}</span></div>}
            {detail.completed_at && <div>Completed: <span className="text-slate-900">{new Date(detail.completed_at).toLocaleString()}</span></div>}
            {detail.cancelled_at && (<>
              <div>Cancelled: <span className="text-slate-900">{new Date(detail.cancelled_at).toLocaleString()}</span></div>
              <div>Reason: <span className="text-slate-900">{detail.cancellation_reason}</span></div>
            </>)}
            {detail.invoice_no && <div>Invoice: <span className="text-slate-900 font-mono">{detail.invoice_no}</span></div>}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
          Line items · {detail.lines.length}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">#</th>
              <th className="text-left px-4 py-2">Passport · Serial</th>
              <th className="text-left px-4 py-2">Description</th>
              <th className="text-right px-4 py-2">Unit</th>
              <th className="text-right px-4 py-2">Disc</th>
              <th className="text-right px-4 py-2">Taxable</th>
              <th className="text-right px-4 py-2">GST</th>
              <th className="text-right px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {detail.lines.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-500">{l.line_no}</td>
                <td className="px-4 py-2">
                  <div className="font-mono text-xs">{l.passport_barcode || "—"}</div>
                  <div className="font-mono text-xs text-slate-500">{l.passport_serial || "—"}</div>
                </td>
                <td className="px-4 py-2 text-slate-800">{l.description}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">₹{l.unit_price}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">₹{l.discount_amount}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">₹{l.taxable_amount}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  ₹{(Number(l.cgst_amount) + Number(l.sgst_amount) + Number(l.igst_amount)).toFixed(2)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs font-semibold">₹{l.line_total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payments */}
      {detail.payments.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
            Payments · {detail.payments.length}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Paid at</th>
                <th className="text-left px-4 py-2">Method</th>
                <th className="text-left px-4 py-2">Reference</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {detail.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(p.paid_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs">{p.method}</td>
                  <td className="px-4 py-2 text-xs font-mono">{p.reference || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">₹{p.amount}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{p.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* V1.2 — Customer Cases section (one row per laptop line, cases grouped by passport) */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="cases-section">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900 flex items-baseline justify-between">
          <span className="inline-flex items-center gap-2"><Wrench size={14}/> Customer Cases</span>
          <span className="text-xs text-slate-500">
            {casesLoading ? "Loading…" : `${cases.length} case${cases.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <ul className="divide-y divide-slate-100">
          {detail.lines.map((l) => {
            const passport = {
              id: l.passport_id, serial: l.passport_serial, barcode: l.passport_barcode,
              // brand/model/etc. aren't returned on lines — best-effort fallback:
              brand: null, model: null, processor: null, ram: null, storage: null,
              generation: null,
            };
            const passportCases = casesByPassport[l.passport_id] || [];
            return (
              <li key={l.id} className="px-5 py-3" data-testid={`cases-row-${l.passport_id || l.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-sm text-slate-800">
                    <span className="font-mono text-xs text-slate-500">L{l.line_no}</span>
                    <span className="ml-2">{l.description}</span>
                    <span className="ml-2 font-mono text-xs text-slate-500">
                      {l.passport_barcode || "—"} · {l.passport_serial || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {passportCases.map((c) => (
                      <button key={c.id} data-testid={`btn-open-case-${c.id}`}
                        onClick={() => navigate(`/admin/cases/${c.id}`)}
                        title={c.complaint || ""}
                        className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-xs text-sky-800 hover:bg-sky-100">
                        <ExternalLink size={12}/> Open {c.case_no}
                        <span className="ml-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{c.status}</span>
                      </button>
                    ))}
                    {canProcure(user) && (
                      <button data-testid={`btn-create-case-${l.passport_id || l.id}`}
                        onClick={() => navigate(buildCaseCreateHref(passport))}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800">
                        <Plus size={12}/> Create New Case
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          {detail.lines.length === 0 && (
            <li className="px-5 py-4 text-sm text-slate-500">No laptop lines on this invoice.</li>
          )}
        </ul>
      </div>

      <Modal title="Permanently delete invoice" open={modal === "delete"} onClose={() => setModal(null)}>
        <div className="space-y-3 text-sm" data-testid="delete-invoice-modal">
          <div>Invoice No: <b data-testid="delete-invoice-no">{detail.invoice_no || "—"}</b> · SO <span className="font-mono">{detail.so_no}</span></div>
          <div>Customer: <b data-testid="delete-invoice-customer">{detail.bill_name || "—"}</b></div>
          <div>Amount: <b data-testid="delete-invoice-amount">₹{detail.grand_total}</b></div>
          <p className="text-rose-700">This permanently removes the invoice and its lines. Customer, laptop passports and stock history are kept. This cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <button data-testid="delete-invoice-close" onClick={() => setModal(null)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Close</button>
            <button data-testid="delete-invoice-confirm" disabled={busy} onClick={deleteOrder}
              className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40">
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Cancel modal */}
      <Modal title="Cancel sales order" open={modal === "cancel"} onClose={() => setModal(null)}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Reason *</span>
            <textarea data-testid={SALES.cancelReason}
              value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setModal(null)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              Close
            </button>
            <button data-testid={SALES.cancelSubmit} disabled={busy || !cancelReason.trim()}
              onClick={cancel}
              className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40">
              {busy ? "Cancelling…" : "Confirm cancellation"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment modal */}
      <Modal title="Record payment" open={modal === "pay"} onClose={() => setModal(null)}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Method</span>
            <select data-testid={SALES.payMethod}
              value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="CHEQUE">Cheque</option>
              <option value="CREDIT">Credit</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Amount *</span>
            <input type="number" min={0} step="0.01"
              data-testid={SALES.payAmount}
              value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            <div className="text-xs text-slate-500 mt-1">Outstanding balance: <span className="font-mono">₹{detail.balance}</span></div>
            {pay.amount && Number(pay.amount) > Number(detail.balance) && (
              <div className="text-xs text-amber-700 mt-1">
                ⚠ Amount exceeds balance — this will mark the order as OVERPAID.
              </div>
            )}
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Reference</span>
            <input data-testid={SALES.payReference}
              value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })}
              placeholder="UPI VPA · Cheque no · Txn ID"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Remarks</span>
            <input data-testid={SALES.payRemarks}
              value={pay.remarks} onChange={(e) => setPay({ ...pay, remarks: e.target.value })}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setModal(null)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              Close
            </button>
            <button data-testid={SALES.paySubmit} disabled={busy || !pay.amount}
              onClick={recordPayment}
              className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* V1.2 — Edit-invoice modal */}
      {edit && (
      <Modal title="Edit Sales Invoice" open={modal === "edit"} onClose={() => setModal(null)}>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Customer type</span>
              <select data-testid="edit-customer-type"
                value={edit.customer_type} onChange={(e) => setEdit({ ...edit, customer_type: e.target.value })}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="WALK_IN">Walk-in</option>
                <option value="RETAIL">Retail</option>
                <option value="CORPORATE">Corporate</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Customer name</span>
              <input data-testid="edit-bill-name"
                value={edit.bill_name} onChange={(e) => setEdit({ ...edit, bill_name: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Mobile</span>
              <input data-testid="edit-bill-mobile"
                value={edit.bill_mobile} onChange={(e) => setEdit({ ...edit, bill_mobile: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Email</span>
              <input data-testid="edit-bill-email"
                value={edit.bill_email} onChange={(e) => setEdit({ ...edit, bill_email: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block col-span-2">
              <span className="block text-xs font-medium text-slate-500 mb-1">Company name</span>
              <input value={edit.bill_company_name}
                onChange={(e) => setEdit({ ...edit, bill_company_name: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block col-span-2">
              <span className="block text-xs font-medium text-slate-500 mb-1">GSTIN (15 chars)</span>
              <input value={edit.bill_gstin}
                onChange={(e) => setEdit({ ...edit, bill_gstin: e.target.value.toUpperCase().slice(0, 15) })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono" />
            </label>
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Billing address</div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Address line 1" value={edit.billing.address_line1}
                onChange={(e) => setEdit({ ...edit, billing: { ...edit.billing, address_line1: e.target.value } })}
                className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" />
              <input placeholder="Address line 2" value={edit.billing.address_line2}
                onChange={(e) => setEdit({ ...edit, billing: { ...edit.billing, address_line2: e.target.value } })}
                className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" />
              <input placeholder="City" value={edit.billing.city}
                onChange={(e) => setEdit({ ...edit, billing: { ...edit.billing, city: e.target.value } })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
              <input placeholder="State" value={edit.billing.state}
                onChange={(e) => setEdit({ ...edit, billing: { ...edit.billing, state: e.target.value } })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
              <input placeholder="Pincode" value={edit.billing.pincode}
                onChange={(e) => setEdit({ ...edit, billing: { ...edit.billing, pincode: e.target.value } })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="flex items-baseline justify-between mb-1">
              <div className="text-xs uppercase tracking-widest text-slate-400">Shipping address</div>
              <button type="button"
                onClick={() => setEdit({ ...edit, shipping: edit.shipping ? null : { name: edit.bill_name, mobile: edit.bill_mobile, address_line1: "", address_line2: "", city: "", state: "", pincode: "" } })}
                className="text-xs text-sky-700 hover:underline">
                {edit.shipping ? "Remove" : "Add different shipping address"}
              </button>
            </div>
            {edit.shipping && (
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Name" value={edit.shipping.name}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, name: e.target.value } })}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Mobile" value={edit.shipping.mobile}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, mobile: e.target.value } })}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Address line 1" value={edit.shipping.address_line1}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, address_line1: e.target.value } })}
                  className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Address line 2" value={edit.shipping.address_line2}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, address_line2: e.target.value } })}
                  className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="City" value={edit.shipping.city}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, city: e.target.value } })}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="State" value={edit.shipping.state}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, state: e.target.value } })}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Pincode" value={edit.shipping.pincode}
                  onChange={(e) => setEdit({ ...edit, shipping: { ...edit.shipping, pincode: e.target.value } })}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </div>
            )}
          </div>

          <label className="block mt-3 border-t border-slate-100 pt-3">
            <span className="block text-xs font-medium text-slate-500 mb-1">Remarks</span>
            <textarea rows={2} value={edit.remarks}
              onChange={(e) => setEdit({ ...edit, remarks: e.target.value })}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>

          {/* V2.8 — DRAFT-only line-items editor. Hidden for CONFIRMED and later. */}
          {detail?.status === "DRAFT" && (
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="edit-lines-section">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Line items</div>

              {editLines.length === 0 && (
                <div className="text-xs text-slate-500 mb-2">No line items yet — search inventory below to add.</div>
              )}
              {editLines.map((l, idx) => (
                <div key={l.passport_id} data-testid={`edit-line-${idx}`}
                  className="grid grid-cols-12 gap-2 items-start mb-2 rounded-md border border-slate-200 p-2">
                  <div className="col-span-5">
                    <div className="text-sm font-medium text-slate-800 truncate">{l.description || "—"}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">
                      {l.passport_barcode || l.passport_serial || l.passport_id}
                    </div>
                  </div>
                  <label className="col-span-3 block">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Selling price</span>
                    <input inputMode="decimal" value={l.unit_price}
                      onChange={(e) => patchEditLine(idx, { unit_price: e.target.value })}
                      placeholder="0.00"
                      data-testid={`edit-line-${idx}-price`}
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm font-mono" />
                  </label>
                  <label className="col-span-3 block">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Discount</span>
                    <input inputMode="decimal" value={l.discount_amount}
                      onChange={(e) => patchEditLine(idx, { discount_amount: e.target.value })}
                      placeholder="0.00"
                      data-testid={`edit-line-${idx}-discount`}
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm font-mono" />
                  </label>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeEditLine(idx)}
                      data-testid={`edit-line-${idx}-remove`}
                      title="Remove line"
                      className="rounded-md border border-rose-200 bg-white p-1.5 text-rose-600 hover:bg-rose-50">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Inventory search + add */}
              <div className="mt-2 rounded-md border border-dashed border-slate-300 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Add product</div>
                <div className="flex gap-2">
                  <input value={invQuery} onChange={(e) => setInvQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchInventory(); } }}
                    placeholder="Search AVAILABLE inventory by barcode, serial, brand, model…"
                    data-testid="edit-inv-query"
                    className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                  <button type="button" onClick={searchInventory} disabled={invSearching}
                    data-testid="edit-inv-search"
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                    {invSearching ? "Searching…" : "Search"}
                  </button>
                </div>
                {invError && (<div className="mt-1 text-xs text-rose-600">{invError}</div>)}
                {invResults.length > 0 && (
                  <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 max-h-40 overflow-auto">
                    {invResults.map((p) => (
                      <li key={p.passport_id} className="flex items-center justify-between px-2 py-1.5 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">
                            {p.brand || ""} {p.model || ""}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono truncate">
                            {p.internal_barcode || p.passport_barcode || ""}
                            {(p.manufacturer_serial || p.passport_serial) ? ` · ${p.manufacturer_serial || p.passport_serial}` : ""}
                          </div>
                        </div>
                        <button type="button" onClick={() => addEditLine(p)}
                          data-testid={`edit-inv-add-${p.passport_id}`}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Live totals */}
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="text-slate-500">Subtotal</div>
                <div className="text-right font-mono" data-testid="edit-total-subtotal">₹ {editTotals.subtotal.toFixed(2)}</div>
                <div className="text-slate-500">Discount</div>
                <div className="text-right font-mono" data-testid="edit-total-discount">− ₹ {editTotals.discount.toFixed(2)}</div>
                <div className="text-slate-500">Taxable</div>
                <div className="text-right font-mono">₹ {editTotals.taxable.toFixed(2)}</div>
                {editTotals.isIgst ? (
                  <>
                    <div className="text-slate-500">IGST</div>
                    <div className="text-right font-mono">₹ {editTotals.igst.toFixed(2)}</div>
                  </>
                ) : (
                  <>
                    <div className="text-slate-500">CGST</div>
                    <div className="text-right font-mono">₹ {editTotals.cgst.toFixed(2)}</div>
                    <div className="text-slate-500">SGST</div>
                    <div className="text-right font-mono">₹ {editTotals.sgst.toFixed(2)}</div>
                  </>
                )}
                <div className="text-slate-900 font-semibold border-t border-slate-100 pt-1">Grand total</div>
                <div className="text-right font-mono font-semibold border-t border-slate-100 pt-1"
                     data-testid="edit-total-grand">₹ {editTotals.grand.toFixed(2)}</div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModal(null)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              Close
            </button>
            <button data-testid="btn-save-invoice-edits" disabled={busy}
              onClick={saveEdit}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </Modal>
      )}
    </div>
  );
}
